import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { Button, Empty, Radio, Space, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useCompany } from '../../hooks/company';
import { useRequest } from 'ahooks';
import {
  BaseDepartmentDTO,
  companyRankController,
  HomeTopDTO,
  Long,
} from '../../api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { flatDepartment } from '../../utils';
import { computeAnnualLevel } from '../../utils/rankLevel';
import RankAvatar from './RankAvatar';
import './rank.scss';

const formatBrokerage = (value?: number | string) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default () => {
  const {
    config: { companyRankConfig } = {},
    id: companyId,
    currentUser: { isSuperAdmin },
    hasPermission,
  } = useCompany();

  const [period, setPeriod] = useState<'month' | 'year'>('month');

  // 全屏展示（大屏投放）
  const pageRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      pageRef.current?.requestFullscreen();
    }
  };

  // 月榜与年榜使用相互独立的权限码
  const permissionCode = (
    p: 'month' | 'year',
    t: 'person' | 'department' | 'big-department',
  ) => (p === 'month' ? `page:rank:${t}` : `page:rank:annual-${t}`);

  const defaultTypeOf = (p: 'month' | 'year') => {
    if (hasPermission(permissionCode(p, 'big-department'))) {
      return 'big-department';
    }
    if (hasPermission(permissionCode(p, 'department'))) {
      return 'department';
    }
    if (hasPermission(permissionCode(p, 'person'))) {
      return 'person';
    }
    return undefined;
  };

  const defaultPermissionType = useMemo(() => defaultTypeOf('month'), [
    hasPermission,
  ]);

  const [type, setType] = useState<
    'person' | 'department' | 'big-department' | undefined
  >(defaultPermissionType);
  const isDama = !isSuperAdmin && type === 'person';

  const hasAnyPermission = (p: 'month' | 'year') =>
    !!defaultTypeOf(p);

  const togglePeriod = () => {
    const next = period === 'month' ? 'year' : 'month';
    setPeriod(next);
    setType(defaultTypeOf(next));
  };

  const {
    data,
    loading,
    refresh: refresh,
  } = useRequest(
    () => {
      return Promise.all(
        Object.entries(companyRankConfig?.companies || {}).map(
          ([rmsCompangId, rmsCompanyName]) => {
            return companyRankController
              .getHomeTop(
                companyId,
                rmsCompangId,
                period === 'year' ? 'year' : undefined,
              )
              .then((item) => ({
                homeTop: item.homeTop?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.userId}`,
                })),
                department: item.departments?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.id}`,
                })),
                users: item.users?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.id}`,
                })),
              }));
          },
        ),
      );
    },
    {
      refreshDeps: [companyRankConfig, companyId, period],
    },
  );

  const rankData = useMemo(() => {
    const homeTops = data?.map((item) => item.homeTop).flat();
    const departments = data
      ?.map((item) => item.department)
      .flat()
      .reduce((prev, cur) => {
        if (cur) {
          prev[cur.rmsCompangId] = [...(prev[cur.rmsCompangId] || []), cur];
        }
        return prev;
      }, {} as Record<Long, BaseDepartmentDTO[]>);

    switch (type) {
      case 'person':
        return homeTops
          ?.sort(
            (a, b) => Number(b?.brokerage || 0) - Number(a?.brokerage || 0),
          )
          .map((item) => {
            const department =
              item?.rmsCompangId && departments
                ? flatDepartment(
                    departments?.[item.rmsCompangId],
                    item.departmentId,
                  ).map((item) => item.name)
                : ['未知部门'];
            return {
              ...item,
              name: item?.userName,
              department: department.reverse(),
            };
          });
      case 'department':
        const values = Object.values(
          homeTops?.reduce((prev, cur) => {
            if (cur) {
              const key = `${cur.rmsCompangId}-${cur.departmentId}`;
              prev[key] = {
                ...cur,
                brokerage:
                  (Number(cur.brokerage) || 0) +
                  (Number(prev[key]?.brokerage) || 0),
                rmsCompanyId: cur.rmsCompangId,
                rmsCompanyName: cur.rmsCompanyName,
                key,
              };
            }
            return prev;
          }, {} as Record<Long, HomeTopDTO & { key: string; rmsCompanyId: Long; rmsCompanyName: string }>) ||
            {},
        );
        return values
          .sort((a, b) => Number(b.brokerage) - Number(a.brokerage))
          .map((item) => {
            const department =
              item?.rmsCompanyId && departments
                ? flatDepartment(
                    departments?.[item.rmsCompanyId],
                    item.departmentId,
                  ).map((item) => item.name)
                : ['未知部门'];
            return {
              ...item,
              name: department[0],
              department: department,
            };
          });
      case 'big-department':
        return data
          ?.map((compamyItem) => compamyItem.department)
          .flat()
          .filter((item) => !item?.parentId)
          .map((currentBigDepartment) => {
            if (!currentBigDepartment?.rmsCompangId) {
              throw new Error('rmsCompangId is required');
            }
            const currentHomeTop = homeTops?.filter(
              (homeTopsItem) =>
                homeTopsItem?.rmsCompangId ===
                currentBigDepartment.rmsCompangId,
            );

            // 找到当前大区所有的子部门
            const childrenDepartmentIds = (
              departments?.[currentBigDepartment.rmsCompangId] || []
            )
              .filter((item) => item.parentId === currentBigDepartment.id)
              .map((item) => item.id);

            // 找到属于这个大区的人的业绩
            const bigDepartmentBrokerage = currentHomeTop
              ?.filter(
                (homeHopItem) =>
                  homeHopItem?.departmentId === currentBigDepartment.id,
              )
              .reduce((prev, cur) => {
                return prev + Number(cur?.brokerage || 0);
              }, 0);
            // 找到属于大区子部门人的业绩
            const bigDepartmentChildrenBrokerage = currentHomeTop
              ?.filter((homeTopsItem) =>
                childrenDepartmentIds.includes(
                  homeTopsItem?.departmentId || '',
                ),
              )
              .reduce((prev, cur) => {
                return prev + Number(cur?.brokerage || 0);
              }, 0);

            const result: HomeTopDTO & {
              department: string[];
              name: string;
              key: string;
              rmsCompanyName: string;
            } = {
              ...currentBigDepartment,
              loanAmount: 0,
              loanCount: 0,
              userId: 0,
              userName: '',
              brokerage:
                (bigDepartmentBrokerage || 0) +
                (bigDepartmentChildrenBrokerage || 0),
              department: [currentBigDepartment.name],
              name: currentBigDepartment.name,
              key: `${currentBigDepartment.rmsCompangId}-${currentBigDepartment.id}`,
              rmsCompanyName: currentBigDepartment.rmsCompanyName,
            };
            return result;
          })
          .sort((a, b) => Number(b.brokerage) - Number(a.brokerage));
    }
  }, [data, type]);

  const top3 = useMemo(() => {
    if (!rankData?.length) {
      return [{ key: '2' }, { key: '1' }, { key: '3' }];
    }

    const value: Partial<(typeof rankData)[0]>[] = [
      { key: '2' },
      { key: '1' },
      { key: '3' },
    ];

    if (rankData?.length > 0) {
      value[1] = rankData[0];
    }
    if (rankData?.length > 1) {
      value[0] = rankData[1];
    }
    if (rankData?.length > 2) {
      value[2] = rankData[2];
    }

    return value;
  }, [rankData]);

  const noPermission = useMemo(() => {
    return (
      !hasPermission('page:rank:person') &&
      !hasPermission('page:rank:department') &&
      !hasPermission('page:rank:big-department') &&
      !hasPermission('page:rank:annual-person') &&
      !hasPermission('page:rank:annual-department') &&
      !hasPermission('page:rank:annual-big-department')
    );
  }, [hasPermission]);

  const enable = useMemo(() => {
    const now = dayjs();
    const week = now.day() === 0 ? 7 : now.day();
    const currentDayTime = companyRankConfig?.time?.[week - 1];
    if (!currentDayTime) {
      return false;
    }
    if (!currentDayTime.enable) {
      return false;
    }
    const startDate = dayjs(
      `${now.format('YYYY-MM-DD')} ${currentDayTime.start}`,
    );
    // 此处原代码存在错误，应该使用 currentDayTime.end 而非 currentDayTime.enable
    const endDate = dayjs(`${now.format('YYYY-MM-DD')} ${currentDayTime.end}`);
    if (now.isBefore(startDate) || now.isAfter(endDate)) {
      return false;
    }
    return true;
  }, [companyRankConfig]);

  const maskName = (name?: string) => {
    if (isDama && name) {
      return name.slice(0, -1) + '*';
    }
    return name;
  };

  // 个人榜姓名前展示员工头像，加载失败或缺失时回退姓名首字
  const renderAvatar = (
    item: { avatarUrl?: string; name?: string },
    className: string,
  ) => {
    if (type !== 'person') {
      return null;
    }
    return (
      <RankAvatar
        avatarUrl={item.avatarUrl}
        name={item.name}
        className={className}
      />
    );
  };

  // 年度个人榜在姓名后展示段位勋章
  const renderLevelMedal = (
    brokerage: number | string | undefined,
    className: string,
  ) => {
    if (period !== 'year' || type !== 'person') {
      return null;
    }
    const medal = computeAnnualLevel(
      Number(brokerage || 0),
      companyRankConfig?.annualLevels,
    );
    return (
      <Tooltip title={`年度段位：${medal.name}`}>
        <img className={className} src={medal.icon} alt={medal.name} />
      </Tooltip>
    );
  };

  const getAffiliation = (item: {
    rmsCompanyName?: string;
    department?: string[];
  }) => {
    switch (type) {
      case 'big-department':
        return item.rmsCompanyName || '';
      case 'department':
        return [item.department?.slice(-1).join('/'), item.rmsCompanyName]
          .filter(Boolean)
          .join(' / ');
      case 'person':
        return [item.department?.join('/'), item.rmsCompanyName]
          .filter(Boolean)
          .join(' / ');
      default:
        return '';
    }
  };

  if (!enable || noPermission) {
    return (
      <Empty
        description={
          <Space>
            <span>榜单暂未开启，敬请期待</span>
            <Button
              type="link"
              onClick={() => {
                refresh();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      />
    );
  }

  if (loading) {
    return (
      <div className="rank-arena-page">
        <div className="rank-arena-loading">
          <Spin spinning={loading} />
        </div>
      </div>
    );
  }

  // top3 数组顺序为 [第二名, 第一名, 第三名]
  const podium = [
    { place: 'first', item: top3[1] },
    { place: 'second', item: top3[0] },
    { place: 'third', item: top3[2] },
  ] as const;

  const listRows = (rankData?.slice(3, 10) || []).map((item) => ({
    ...item,
    brokerage: Number(item.brokerage || 0),
  }));

  return (
    <div className="rank-arena-page" ref={pageRef}>
      <div className="rank-arena">
        <h1 className="rank-arena-title">龙虎榜单</h1>
        <Tooltip title={isFullscreen ? '退出全屏' : '全屏展示'}>
          <button
            type="button"
            className="rank-arena-fullscreen"
            aria-label={isFullscreen ? '退出全屏' : '全屏展示'}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          </button>
        </Tooltip>
        <span className="rank-arena-month">
          {period === 'year' ? '年度榜单' : `${dayjs().month() + 1}月榜单`}
        </span>
        {hasAnyPermission(period === 'month' ? 'year' : 'month') && (
          <button
            type="button"
            className="rank-arena-period-toggle"
            onClick={togglePeriod}
          >
            {period === 'month' ? '年度榜单' : '月榜单'}
          </button>
        )}
        <Radio.Group
          className="rank-arena-tabs"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <Radio.Button
            disabled={!hasPermission(permissionCode(period, 'person'))}
            value="person"
          >
            个人
          </Radio.Button>
          <Radio.Button
            disabled={!hasPermission(permissionCode(period, 'department'))}
            value="department"
          >
            部门
          </Radio.Button>
          <Radio.Button
            disabled={!hasPermission(permissionCode(period, 'big-department'))}
            value="big-department"
          >
            大区
          </Radio.Button>
        </Radio.Group>
        {podium.map(({ place, item }) => (
          <div className={`rank-badge rank-badge-${place}`} key={place}>
            <div className="rank-badge-name">
              {renderAvatar(item, 'rank-badge-avatar')}
              {maskName('name' in item ? item.name : undefined)}
              {'brokerage' in item &&
                renderLevelMedal(item.brokerage, 'rank-badge-medal')}
            </div>
            <div className="rank-badge-affiliation">{getAffiliation(item)}</div>
            <div className="rank-badge-amount">
              {'brokerage' in item ? formatBrokerage(item.brokerage) : ''}
            </div>
          </div>
        ))}
        <div className="rank-table-head">
          <span className="rank-cell rank-cell-rank">排名</span>
          <span className="rank-cell rank-cell-name">
            {type === 'person' ? '姓名' : '名称'}
          </span>
          <span className="rank-cell rank-cell-affiliation">部门 / 公司</span>
          <span className="rank-cell rank-cell-amount">
            {period === 'year' ? '年度业绩' : '本月业绩'}
          </span>
        </div>
        {listRows.map((item, index) => (
          <div
            className="rank-list-row"
            key={item.key}
            style={{ top: `${55.02 + index * 5.404}%` }}
          >
            <span className="rank-cell rank-cell-rank">{index + 4}</span>
            <span className="rank-cell rank-cell-name">
              {renderAvatar(item, 'rank-row-avatar')}
              {maskName(item.name)}
              {renderLevelMedal(item.brokerage, 'rank-row-medal')}
            </span>
            <span className="rank-cell rank-cell-affiliation">
              {getAffiliation(item)}
            </span>
            <span className="rank-cell rank-cell-amount">
              {formatBrokerage(item.brokerage)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
